export type OutreachStatus = "todo" | "draft" | "sent" | "positive" | "negative";

export type LeadStatus = "in_progress" | "no_response" | "positive_response" | "negative_response";

export const DEFAULT_LEAD_STATUS: LeadStatus = "in_progress";

export const LEAD_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
    { value: "in_progress", label: "In Progress" },
    { value: "no_response", label: "No Response" },
    { value: "positive_response", label: "Positive Response" },
    { value: "negative_response", label: "Negative Response" },
];

export function isLeadStatus(value: string): value is LeadStatus {
    return LEAD_STATUS_OPTIONS.some((option) => option.value === value);
}

/** Map legacy todo / done rows onto the current lead statuses. */
export function normalizeLeadStatus(value: string | null | undefined): LeadStatus {
    if (value && isLeadStatus(value)) return value;
    if (value === "done") return "positive_response";
    return DEFAULT_LEAD_STATUS;
}

export type LeadEmailStatus = "not_started" | "found" | "not_found" | "error";

export const DEFAULT_LEAD_EMAIL_STATUS: LeadEmailStatus = "not_started";

export const LEAD_EMAIL_STATUS_OPTIONS: { value: LeadEmailStatus; label: string }[] = [
    { value: "not_started", label: "Not tried" },
    { value: "found", label: "Found" },
    { value: "not_found", label: "Not found" },
    { value: "error", label: "Error" },
];

export function isLeadEmailStatus(value: string): value is LeadEmailStatus {
    return LEAD_EMAIL_STATUS_OPTIONS.some((option) => option.value === value);
}

export function normalizeLeadEmailStatus(value: string | null | undefined, email?: string | null): LeadEmailStatus {
    if (value && isLeadEmailStatus(value)) return value;
    if (email?.trim()) return "found";
    return DEFAULT_LEAD_EMAIL_STATUS;
}

export function leadEmailStatusLabel(status: LeadEmailStatus): string {
    return LEAD_EMAIL_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

export type OutboundTouchKind = "connect" | "email" | "linkedin";
export type ReplyTouchKind = "email_reply" | "linkedin_reply";
export type TouchKind = OutboundTouchKind | ReplyTouchKind;

export function isTouchReplyKind(kind: TouchKind): kind is ReplyTouchKind {
    return kind === "email_reply" || kind === "linkedin_reply";
}

export function isOutboundTouchKind(kind: TouchKind): kind is OutboundTouchKind {
    return kind === "connect" || kind === "email" || kind === "linkedin";
}

export function isReplyStatus(status: OutreachStatus): status is "positive" | "negative" {
    return status === "positive" || status === "negative";
}

export function isOutboundStatus(status: OutreachStatus): status is "todo" | "draft" | "sent" {
    return status === "todo" || status === "draft" || status === "sent";
}

/** Lead status to set when logging a reply touch. */
export function leadStatusSyncedFromReply(touch: Touch): LeadStatus | null {
    if (!isTouchReplyKind(touch.kind)) return null;
    if (touch.status === "positive") return "positive_response";
    if (touch.status === "negative") return "negative_response";
    return null;
}

/** Coerce status/message when the user changes touch kind. */
export function touchWithKind(touch: Touch, kind: TouchKind, today = new Date().toISOString().slice(0, 10)): Touch {
    if (isTouchReplyKind(kind)) {
        return {
            ...touch,
            kind,
            status: isReplyStatus(touch.status) ? touch.status : "positive",
            date: touch.date ?? today,
        };
    }
    if (kind === "connect") {
        return {
            ...touch,
            kind,
            message: "",
            status: touch.date ? "sent" : "todo",
        };
    }
    return {
        ...touch,
        kind,
        status: isOutboundStatus(touch.status) ? touch.status : "todo",
    };
}

/** Apply a touch update and sync lead status when the touch is a reply. */
export function applyTouchChange(lead: Lead, next: Touch): Lead {
    const exists = lead.touches.some((touch) => touch.touch_id === next.touch_id);
    const touches = sortTouchesByDate(
        exists
            ? lead.touches.map((touch) => (touch.touch_id === next.touch_id ? next : touch))
            : [...lead.touches, next]
    );
    return {
        ...lead,
        touches,
        status: leadStatusSyncedFromReply(next) ?? lead.status,
    };
}

export interface Touch {
    touch_id: string;
    kind: TouchKind;
    date: string | null;
    status: OutreachStatus;
    message: string;
}

/** Default headings we research and store as company context notes. Extra titles are allowed. */
export const COMPANY_CONTEXT_TITLES = [
    "Therapeutic areas",
    "New Product Launches",
    "Relevant PROMs",
    "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
] as const;

export type CompanyContextTitle = (typeof COMPANY_CONTEXT_TITLES)[number];

const COMPANY_CONTEXT_TITLE_ALIASES: Record<string, CompanyContextTitle> = {
    "therapeutic areas": "Therapeutic areas",
    "new product launches": "New Product Launches",
    "new products": "New Product Launches",
    launches: "New Product Launches",
    "relevant proms": "Relevant PROMs",
    proms: "Relevant PROMs",
    prms: "Relevant PROMs",
    "prms and proms": "Relevant PROMs",
    "relevant prms": "Relevant PROMs",
    "regulatory and compliance": "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
    pharmacovigilance: "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
    pv: "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
    pms: "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
    pmcf: "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
    "pms/pmcf": "Regulatory and compliance (Pharmacovigilance or PMS/PMCF needs)",
};

export interface CompanyContextNote {
    context_id: string;
    /** Usually one of COMPANY_CONTEXT_TITLES. */
    title: string;
    body: string;
}

export interface Company {
    company_id: string;
    name: string;
    /** Public website for the organisation. */
    url: string;
    /** Specialties or product areas at this company. Blank on a lead means the whole company is one cohort. */
    teams: string[];
    /** Research notes we can mix into Gen Touches. */
    contexts: CompanyContextNote[];
}

export function createLeadGenId(): string {
    return crypto.randomUUID();
}

export function createCompanyId(): string {
    return createLeadGenId();
}

export function createCompanyContextNote(body = "", title = ""): CompanyContextNote {
    return {
        context_id: createLeadGenId(),
        title: canonicalCompanyContextTitle(title),
        body,
    };
}

export function normalizeCompanyContexts(value: unknown): CompanyContextNote[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((item) => {
        if (typeof item === "string") {
            const body = item.trim();
            return body ? [createCompanyContextNote(body)] : [];
        }
        if (!item || typeof item !== "object") return [];
        const record = item as { context_id?: unknown; title?: unknown; body?: unknown };
        const title = typeof record.title === "string" ? canonicalCompanyContextTitle(record.title) : "";
        const body = typeof record.body === "string" ? record.body : "";
        if (!title.trim() && !body.trim()) return [];
        return [
            {
                context_id:
                    typeof record.context_id === "string" && record.context_id ? record.context_id : createLeadGenId(),
                title,
                body,
            },
        ];
    });
}

export function canonicalCompanyContextTitle(title: string): string {
    const trimmed = title.trim();
    if (!trimmed) return "";
    const exact = COMPANY_CONTEXT_TITLES.find(
        (item) => item.localeCompare(trimmed, undefined, { sensitivity: "base" }) === 0
    );
    if (exact) return exact;
    return COMPANY_CONTEXT_TITLE_ALIASES[trimmed.toLocaleLowerCase()] ?? trimmed;
}

export function nextCompanyContextTitle(notes: CompanyContextNote[]): string {
    const used = new Set(
        notes.map((note) => canonicalCompanyContextTitle(note.title).toLocaleLowerCase()).filter(Boolean)
    );
    return COMPANY_CONTEXT_TITLES.find((title) => !used.has(title.toLocaleLowerCase())) ?? "";
}

export function mergeCompanyContextResearch(
    existing: CompanyContextNote[],
    incoming: CompanyContextNote[]
): CompanyContextNote[] {
    const next = existing.map((note) => ({
        ...note,
        title: canonicalCompanyContextTitle(note.title) || note.title,
    }));

    for (const note of incoming) {
        const title = canonicalCompanyContextTitle(note.title) || note.title.trim();
        const body = note.body.trim();
        if (!title && !body) continue;
        const index = title
            ? next.findIndex((item) => item.title.localeCompare(title, undefined, { sensitivity: "base" }) === 0)
            : -1;
        if (index === -1) {
            next.push({ ...note, title, body });
            continue;
        }
        if (body) {
            next[index] = { ...next[index]!, title, body };
        }
    }

    return next;
}

export function findCompanyForLead(companies: Company[], lead: Pick<Lead, "company">): Company | undefined {
    const name = lead.company.trim();
    if (!name) return undefined;
    return companies.find((item) => item.name.localeCompare(name, undefined, { sensitivity: "base" }) === 0);
}

export function usableCompanyContexts(notes: CompanyContextNote[]): CompanyContextNote[] {
    return notes.filter((note) => note.body.trim());
}

export function formatCompanyContextSummary(notes: CompanyContextNote[]): string {
    const count = usableCompanyContexts(notes).length;
    if (count === 0) return "No notes";
    return count === 1 ? "1 note" : `${count} notes`;
}

export function formatCompanyContexts(notes: CompanyContextNote[]): string {
    const usable = notes.filter((note) => note.body.trim() || note.title.trim());
    if (!usable.length) return "";
    return usable
        .map((note, index) => {
            const title = note.title.trim();
            const body = note.body.trim();
            if (title && body) return `${index + 1}. ${title}\n${body}`;
            return `${index + 1}. ${title || body}`;
        })
        .join("\n\n");
}

export function normalizeCompanyUrl(value: unknown): string {
    if (typeof value !== "string") return "";
    return value.trim();
}

export function createCompany(
    name: string,
    teams: string[] = [],
    companyId = createCompanyId(),
    contexts: CompanyContextNote[] = [],
    url = ""
): Company {
    const trimmed = name.trim();
    return {
        company_id: companyId,
        name: trimmed,
        url: normalizeCompanyUrl(url),
        teams: Array.from(new Set(teams.map((team) => team.trim()).filter(Boolean))).toSorted((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
        ),
        contexts: normalizeCompanyContexts(contexts),
    };
}

export const OUTBOUND_TOUCH_KIND_OPTIONS: { value: OutboundTouchKind; label: string }[] = [
    { value: "connect", label: "Connect on LinkedIn" },
    { value: "email", label: "Email" },
    { value: "linkedin", label: "LinkedIn message" },
];

export const REPLY_TOUCH_KIND_OPTIONS: { value: ReplyTouchKind; label: string }[] = [
    { value: "email_reply", label: "Email reply" },
    { value: "linkedin_reply", label: "LinkedIn reply" },
];

export const TOUCH_KIND_OPTIONS: { value: TouchKind; label: string }[] = [
    ...OUTBOUND_TOUCH_KIND_OPTIONS,
    ...REPLY_TOUCH_KIND_OPTIONS,
];

export interface Lead {
    lead_id: string;
    name: string;
    /** Job title at the company (e.g. Marketing Lead). Functions such as marketing belong here. */
    role: string;
    company: string;
    /** Therapeutic specialty or product area (e.g. Cardiology). Empty means the whole company is one cohort. */
    team: string;
    linkedin_url: string;
    /** Verified work email from enrichment (Prospeo). */
    email: string;
    /** Whether Prospeo (or a manual entry) has already resolved this email. */
    email_status: LeadEmailStatus;
    /** Artefact-specific outreach angle from a pasted profile. */
    summary: string;
    status: LeadStatus;
    touches: Touch[];
}

export const OUTREACH_STATUS_OPTIONS: { value: OutreachStatus; label: string }[] = [
    { value: "todo", label: "Todo" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
];

export const REPLY_STATUS_OPTIONS: { value: OutreachStatus; label: string }[] = [
    { value: "positive", label: "Positive" },
    { value: "negative", label: "Negative" },
];

export function createTouchId(): string {
    return createLeadGenId();
}

export function emptyTouch(kind: TouchKind = "email"): Touch {
    if (isTouchReplyKind(kind)) {
        return {
            touch_id: createTouchId(),
            kind,
            date: new Date().toISOString().slice(0, 10),
            status: "positive",
            message: "",
        };
    }
    return { touch_id: createTouchId(), kind, date: null, status: "todo", message: "" };
}

export function addTouchToLead(leads: Lead[], leadId: string, kind: TouchKind = "email"): Lead[] {
    return leads.map((lead) => {
        if (lead.lead_id !== leadId) return lead;
        return applyTouchChange(lead, emptyTouch(kind));
    });
}

export function outreachStatusLabel(status: OutreachStatus): string {
    return (
        OUTREACH_STATUS_OPTIONS.find((option) => option.value === status)?.label ??
        REPLY_STATUS_OPTIONS.find((option) => option.value === status)?.label ??
        status
    );
}

/** Other people being contacted at the same company (AI can reference them). */
export function leadColleagues(leads: Lead[], lead: Lead): { name: string; role: string }[] {
    if (!lead.company.trim()) return [];
    return leads
        .filter(
            (other) =>
                other.lead_id !== lead.lead_id &&
                other.company.localeCompare(lead.company, undefined, { sensitivity: "base" }) === 0
        )
        .map((other) => ({ name: other.name, role: other.role }));
}

export function touchKindLabel(kind: TouchKind): string {
    return TOUCH_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
}

export function leadStatusLabel(status: LeadStatus): string {
    return LEAD_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

const TOUCH_KIND_SORT_ORDER: Record<TouchKind, number> = {
    connect: 0,
    linkedin: 1,
    email: 2,
    linkedin_reply: 3,
    email_reply: 4,
};

/** Dated touches first (ascending), undated last. Same-day order: connect, LinkedIn, email. */
export function sortTouchesByDate(touches: Touch[]): Touch[] {
    return touches.toSorted((a, b) => {
        if (!a.date && !b.date) return TOUCH_KIND_SORT_ORDER[a.kind] - TOUCH_KIND_SORT_ORDER[b.kind];
        if (!a.date) return 1;
        if (!b.date) return -1;
        const byDate = a.date.localeCompare(b.date);
        if (byDate !== 0) return byDate;
        return TOUCH_KIND_SORT_ORDER[a.kind] - TOUCH_KIND_SORT_ORDER[b.kind];
    });
}
